import React from 'react';
import { Car, Users, FileText, BarChart3, MapPin, Shield, TrendingUp, AlertTriangle } from 'lucide-react';

const Dashboard: React.FC = () => {
  const stats = [
    {
      title: 'Vehículos Registrados',
      value: '12,543',
      change: '+5.2%',
      changeType: 'positive',
      icon: Car,
      color: 'bg-blue-500'
    },
    {
      title: 'Licencias Activas',
      value: '8,921',
      change: '+2.1%',
      changeType: 'positive',
      icon: Users,
      color: 'bg-green-500'
    },
    {
      title: 'Trámites Pendientes',
      value: '234',
      change: '-12.3%',
      changeType: 'negative',
      icon: FileText,
      color: 'bg-yellow-500'
    },
    {
      title: 'Infracciones',
      value: '1,456',
      change: '+8.7%',
      changeType: 'negative',
      icon: AlertTriangle,
      color: 'bg-red-500'
    }
  ];

  const recentActivities = [
    {
      id: 1,
      type: 'Matrícula',
      description: 'Nueva matrícula para vehículo ABC-123',
      user: 'María González',
      time: 'Hace 5 minutos',
      status: 'completed'
    },
    {
      id: 2,
      type: 'Traspaso',
      description: 'Traspaso de propiedad XYZ-789',
      user: 'Carlos Rodríguez',
      time: 'Hace 15 minutos',
      status: 'pending'
    },
    {
      id: 3,
      type: 'Licencia',
      description: 'Renovación de licencia C1',
      user: 'Ana Martínez',
      time: 'Hace 30 minutos',
      status: 'completed'
    },
    {
      id: 4,
      type: 'Infracción',
      description: 'Nueva infracción registrada',
      user: 'Sistema',
      time: 'Hace 1 hora',
      status: 'warning'
    }
  ];

  const quickActions = [
    {
      title: 'Nueva Matrícula',
      description: 'Registrar vehículo nuevo',
      icon: Car,
      color: 'bg-blue-500',
      href: '/tramites/automotor/matricula'
    },
    {
      title: 'Consulta RUNT',
      description: 'Consultar información vehicular',
      icon: Shield,
      color: 'bg-green-500',
      href: '/consultas-reportes/runt'
    },
    {
      title: 'Generar Reporte',
      description: 'Crear reporte estadístico',
      icon: BarChart3,
      color: 'bg-purple-500',
      href: '/consultas-reportes/generar'
    },
    {
      title: 'Ver Mapas',
      description: 'Visualizar rutas y servicios',
      icon: MapPin,
      color: 'bg-orange-500',
      href: '/mapas'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Bienvenido al sistema CiviTransito</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className={`text-sm ${
                  stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                }`}>
                  <TrendingUp className="inline w-4 h-4 mr-1" />
                  {stat.change}
                </p>
              </div>
              <div className={`${stat.color} p-3 rounded-full`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activities */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Actividad Reciente</h3>
          <div className="space-y-4">
            {recentActivities.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3">
                <div className={`w-2 h-2 rounded-full mt-2 ${
                  activity.status === 'completed' ? 'bg-green-500' :
                  activity.status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'
                }`}></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{activity.type}</p>
                  <p className="text-sm text-gray-600">{activity.description}</p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-gray-500">{activity.user}</p>
                    <p className="text-xs text-gray-500">{activity.time}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Acciones Rápidas</h3>
          <div className="grid grid-cols-2 gap-4">
            {quickActions.map((action, index) => (
              <button
                key={index}
                className="p-4 text-left rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className={`${action.color} w-8 h-8 rounded-lg flex items-center justify-center mb-2`}>
                  <action.icon className="w-4 h-4 text-white" />
                </div>
                <h4 className="text-sm font-medium text-gray-900">{action.title}</h4>
                <p className="text-xs text-gray-600">{action.description}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Estado del Sistema</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-700">API RUNT: Conectada</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-700">API SIMIT: Conectada</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-700">Base de Datos: Activa</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
